import { Box, Center, Text, VStack } from "@chakra-ui/layout";
import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Select,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { Spinner } from "@chakra-ui/spinner";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../Card/Card";
import { SearchInput } from "../SearchInput/SearchInput";
import { useSearchInputState } from "../../hooks/useSearchInputState";
import { usePaginatedApiRequest } from "../../hooks/usePaginatedApiRequest";
import { ErrorMessage } from "../ErrorMessage/ErrorMessage";
import { PlusIcon, TabItemSelectIcon } from "../../styles/icons";
import { EventDetailCard } from "../EventDetailCard/EventDetailCard";
import { useLayoutContext } from "../../context/LayoutContext";
import DateUtil from "../../util/DateUtil";
import EventUtil from "../../util/EventUtil";
import { CherryPayApi } from "../../api/models";
import { EventPlaceholder } from "./EventPlaceholder";
import {
  EventSortByLabels,
  EventSortByOptions,
  EventStatuses,
} from "./EventModels";
import { useIsSuperAdminOrSuperUser } from "../../context/UserContext";

export const Events = ({ businessId }: { businessId: string }) => {
  const { query, debouncedQuery, onChange, isDebouncing, setQuery } =
    useSearchInputState("");
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [selectedEvent, setSelectedEvent] = useState<CherryPayApi.Event | null>(
    null
  );
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [eventStatus, setEventStatus] = useState<string>("All Statuses");
  const [sortBy, setSortBy] = useState<string>("LastModifiedNewestFirst");
  const [eventDate, setEventDate] = useState<string>("");
  const navigate = useNavigate();
  const { isDesktop } = useLayoutContext();
  const isSuperAdminOrSuperUser = useIsSuperAdminOrSuperUser();

  // Get current date in YYYY-MM-DD format for queries
  const today = useMemo(() => DateUtil.getDatePartOnly(new Date()), []);

  const tomorrow = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return DateUtil.getDatePartOnly(date);
  }, []);

  const nextWeek = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return DateUtil.getDatePartOnly(date);
  }, []);

  const eventStatusOptions: string[] = useMemo(() => {
    return [
      "All Statuses",
      ...Object.values(EventStatuses).filter((status) => status !== "Deleted"),
    ];
  }, []);

  // Create query parameters based on selected tab
  const queryParams = useMemo(() => {
    const baseParams: {
      startDateFrom?: string;
      startDateTo?: string;
      endDateFrom?: string;
      endDateTo?: string;
      status?: CherryPayApi.EventStatus;
      sortBy?: CherryPayApi.EventSortByOption;
    } = {
      startDateFrom: undefined,
      startDateTo: undefined,
      endDateFrom: undefined,
      endDateTo: undefined,
      status: undefined,
      sortBy: undefined,
    };

    switch (selectedTab) {
      case 1: // Today
        baseParams.startDateTo = today;
        baseParams.endDateFrom = today;
        break;
      case 2: // Upcoming
        baseParams.startDateFrom = tomorrow;
        baseParams.endDateTo = nextWeek;
        break;
      default:
        // All
        break;
    }

    // Apply date filter if date is selected
    if (eventDate) {
      baseParams.startDateTo = eventDate;
      baseParams.endDateFrom = eventDate;
    }

    if (eventStatus !== "All Statuses") {
      baseParams.status = eventStatus as CherryPayApi.EventStatus;
    }

    baseParams.sortBy = sortBy as CherryPayApi.EventSortByOption;

    return baseParams;
  }, [selectedTab, today, tomorrow, nextWeek, eventStatus, sortBy, eventDate]);

  // Fetch events with appropriate filters
  const {
    items: events,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedApiRequest(
    (apiClient) =>
      apiClient.searchEventsQuery(
        businessId,
        debouncedQuery,
        undefined,
        30,
        queryParams.startDateFrom,
        queryParams.startDateTo,
        queryParams.endDateFrom,
        queryParams.endDateTo,
        queryParams.status,
        queryParams.sortBy
      ),
    (apiClient, continuationToken) =>
      apiClient.searchEventsQuery(
        businessId,
        debouncedQuery,
        continuationToken,
        30,
        queryParams.startDateFrom,
        queryParams.startDateTo,
        queryParams.endDateFrom,
        queryParams.endDateTo,
        queryParams.status,
        queryParams.sortBy
      ),
    [businessId, debouncedQuery, queryParams]
  );

  // Handle event selection
  const handleEventClick = (event: CherryPayApi.Event) => {
    if (!isSuperAdminOrSuperUser) {
      // For staff members, open the scan URL in current tab
      window.location.href = EventUtil.getEventScanUrl(event);
      return;
    }

    setSelectedEvent(event);

    if (!isDesktop) {
      setShowDetail(true);
    } else {
      // Scroll to the top of the page to ensure the preview is visible
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Go back to list view (mobile only)
  const handleBackClick = () => {
    setShowDetail(false);
  };

  // Render the event list
  const renderEventList = () => {
    if (isLoading && (!events || events.length === 0)) {
      return (
        <Center p={8}>
          <Spinner />
        </Center>
      );
    }

    if (events?.length === 0) {
      return (
        <Center p={8}>
          <Text>
            {debouncedQuery === ""
              ? "No events found."
              : `No events found matching query "${debouncedQuery}".`}
          </Text>
        </Center>
      );
    }

    return (
      <VStack spacing={4} align="stretch" w="100%">
        {events?.map((event) => {
          const isSelected = selectedEvent?.id === event.id;
          const bgColor = isSelected
            ? "cherryBackground.selectedTabItem"
            : "white";
          const iconColor = isSelected
            ? "cherryUi.tabIconSelected"
            : "gray.500";
          const borderLeft = isSelected ? "4px solid" : "none";

          return (
            <Card
              key={event.id}
              p={"10px 20px"}
              m={"0"}
              mt={"0 !important"}
              borderBottomWidth={"1px"}
              cursor="pointer"
              onClick={() => handleEventClick(event)}
              bg={bgColor}
              borderLeft={borderLeft}
              borderLeftColor="cherryUi.600"
            >
              <HStack
                justify="space-between"
                align="start"
                alignItems="center"
                w="100%"
              >
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">{event.EventName}</Text>
                  <Text fontSize="sm">
                    Date:{" "}
                    {DateUtil.formatDateRange(
                      event.EventStartDate,
                      event.EventEndDate
                    )}{" "}
                    &nbsp;&nbsp;
                    {DateUtil.formatTimeRange(
                      event.CheckInStartTime,
                      event.CheckInEndTime
                    )}
                  </Text>
                  <Text fontSize="sm">
                    Check-In:&nbsp;
                    {DateUtil.formatTimeRange(
                      event.CheckInStartTime,
                      event.CheckInEndTime
                    )}
                  </Text>
                </VStack>

                <HStack align="center" spacing={2}>
                  <Badge
                    px={3}
                    py={1}
                    borderRadius="full"
                    background={EventUtil.getStatusColorScheme(
                      event.EventStatus
                    )}
                    fontSize="xs"
                    fontWeight="bold"
                    color={"white"}
                    textTransform="uppercase"
                  >
                    {event.EventStatus}
                  </Badge>
                  <TabItemSelectIcon color={iconColor} fontSize={"20px"} />
                </HStack>
              </HStack>
            </Card>
          );
        })}

        {fetchNextPage && (
          <Center p={2}>
            {!isLoadingPage ? (
              <Button
                colorScheme="cherryButton"
                onClick={fetchNextPage}
                size="sm"
              >
                Load more
              </Button>
            ) : (
              <Spinner size="sm" />
            )}
          </Center>
        )}
      </VStack>
    );
  };

  // Mobile view with back button when showing detail
  if (!isDesktop && showDetail && selectedEvent) {
    return (
      <VStack width="100%" spacing={4} align="stretch">
        <HStack width="100%" pl={"20px"} pt={"20px"}>
          <Link
            to="#"
            onClick={handleBackClick}
            style={{ alignSelf: "flex-start", marginBottom: "10px" }}
          >
            &lt; Back to All Events
          </Link>
        </HStack>
        <Box
          height="100%"
          mt={0}
          p={4}
          borderLeft={"1px solid"}
          borderColor="gray.200"
          boxShadow={"md"}
        >
          <Card width="100%">
            <EventDetailCard event={selectedEvent} miniMode={true} />
          </Card>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack width="100%" spacing={4}>
      <Card width="100%" padding={"0"}>
        <VStack width="100%" spacing={4} alignItems="start">
          {isSuperAdminOrSuperUser && (
            <HStack
              width="100%"
              paddingBottom={"10px"}
              borderBottomWidth="1px"
              borderColor={"cherryBorder.100"}
              sx={{ gap: "10px" }}
            >
              <Box width="250px" marginLeft={"20px"}>
                <SearchInput
                  value={query}
                  onChange={onChange}
                  isLoading={isLoading || isDebouncing}
                  placeholder="Search Events"
                  setQuery={setQuery}
                />
              </Box>
              <Box width="200px">
                <Select
                  value={eventStatus}
                  onChange={(e) => setEventStatus(e.target.value)}
                >
                  {eventStatusOptions.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box width="200px">
                <FormControl>
                  <Input
                    type="date"
                    placeholder="Filter by date"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                  />
                </FormControl>
              </Box>
              <Box width="300px">
                <FormControl display={"flex"} alignItems={"center"}>
                  <FormLabel width={"80px"} mb={"0"} mr={"0"}>
                    Sort by
                  </FormLabel>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    {EventSortByOptions.map((option) => (
                      <option key={option} value={option}>
                        {option in EventSortByLabels
                          ? EventSortByLabels[
                              option as keyof typeof EventSortByLabels
                            ]
                          : option}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              {isSuperAdminOrSuperUser && (
                <Button
                  leftIcon={<PlusIcon />}
                  onClick={() =>
                    navigate(`/businesses/${businessId}/events/new`)
                  }
                  colorScheme="cherryButton"
                  marginInlineEnd={"30px !important"}
                  marginLeft={"auto !important"}
                  whiteSpace="normal"
                  height="auto"
                  py={2}
                >
                  Create New Event
                </Button>
              )}
            </HStack>
          )}

          <Flex
            w="100%"
            direction={isDesktop ? "row" : "column"}
            marginTop={"0px !important"}
            background={"cherryBackground.placeholder"}
          >
            <Box
              w={isDesktop ? "30%" : "100%"}
              pr={0}
              background={"white"}
              minH={"600px"}
            >
              <Tabs onChange={setSelectedTab} colorScheme="cherryUi" w="100%">
                <TabList width="100%" justifyContent={"space-around"}>
                  <Tab width="100%">All</Tab>
                  <Tab width="100%">Today</Tab>
                  <Tab width="100%">Upcoming</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel p={0}>{renderEventList()}</TabPanel>
                  <TabPanel p={0}>{renderEventList()}</TabPanel>
                  <TabPanel p={0}>{renderEventList()}</TabPanel>
                </TabPanels>
              </Tabs>
            </Box>

            {isDesktop && (
              <Box
                w="70%"
                height="100%"
                m={8}
                p={10}
                borderLeft={selectedEvent ? "1px solid" : "none"}
                borderColor="gray.200"
                background={selectedEvent ? "white" : undefined}
                boxShadow={selectedEvent ? "md" : undefined}
              >
                {selectedEvent ? (
                  <EventDetailCard event={selectedEvent} miniMode={true} />
                ) : (
                  <EventPlaceholder />
                )}
              </Box>
            )}
          </Flex>

          {error != null && (
            <ErrorMessage>
              An error was encountered while searching events.
            </ErrorMessage>
          )}
        </VStack>
      </Card>
    </VStack>
  );
};
